import { Component, HostBinding, OnInit } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { SwitchInverse, SwitchVariant } from '@frontend/sports/common/ui/sports-themed-switch';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { IUserBettingSettings, SyncUserSettingsService, UserSettingsResponse, UserSettingsService } from '@frontend/sports/user/feature';
import { MessageQueueService } from '@frontend/vanilla/core';

import { OverAskMessagesProvider } from '../../../services/over-ask-messages.provider';

@Component({
    selector: 'bs-digital-over-ask-stakechange-confirmation',
    templateUrl: './betslip-over-ask-stakechange-confirmation.component.html',
    styleUrls: ['./betslip-over-ask-stakechange-confirmation.component.scss'],
    standalone: false,
})
export class BetSlipOverAskStakeChangeConfirmationComponent implements OnInit {
    protected readonly logger: SportsRemoteLogger;
    settings: IUserBettingSettings;
    readonly messages: {
        maxStakeHeader: string;
        maxStakeMsg: string;
        maxStakeToggle: string;
    };

    readonly variant: SwitchVariant = {
        'default': 'utility',
        'bwin-dark': 'neutral',
        'sportingbet-2': 'neutral',
    };

    readonly themeInverseIndicator: SwitchInverse = {
        'sportingbet-2': { isInverse: true },
    };

    @HostBinding('class') className = 'betslip-over-ask-stakechange-confirmation';

    constructor(
        private sitecore: Sitecore,
        protected userSettings: UserSettingsService,
        protected messageQueueService: MessageQueueService,
        overAskMessagesProvider: OverAskMessagesProvider,
        protected trackingService: TrackingService,
        private settingsService: SyncUserSettingsService,
    ) {
        this.messages = {
            maxStakeHeader: overAskMessagesProvider.messages.stakeChangeHeader,
            maxStakeMsg: overAskMessagesProvider.messages.stakeChangeMsg,
            maxStakeToggle: overAskMessagesProvider.messages.stakeChangeToggle,
        };
    }

    ngOnInit(): void {
        this.trackStakeChange();
        this.settings = Object.assign({ stakeChangeConsent: 0 }, this.settingsService.bettingSettings);
    }

    async save(value: boolean): Promise<void> {
        this.settings.stakeChangeConsent = value ? 1 : 0;
        try {
            const result = await this.userSettings.set({ stakeChangeConsent: this.settings.stakeChangeConsent });
            this.handleResponse(result);
        } catch (error) {
            this.logger.error(error, 'Failed to save user settings');

            this.messageQueueService.addError(this.sitecore.bettingPreferences.UpdateFail);
        }
        this.trackSaveToggle();
    }

    protected handleResponse(result: UserSettingsResponse): void {
        this.messageQueueService.addSuccess(this.sitecore.bettingPreferences.UpdateSuccess);
    }

    private trackStakeChange() {
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betslip',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'overask',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: 'overask in-play',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'always accept max stake',
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    private trackSaveToggle() {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betslip',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'overask',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.settings.stakeChangeConsent ? 'toggle on' : 'toggle off',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'First Time Max Stake Exceeded',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: 'overask in-play',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'always accept max stake',
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }
}
